import type { PayPeriodRecordMap } from '@libs/data-access/entities'
import type { PayPeriodSchema } from '@libs/data-access/queries'
import momentJs from 'moment'
import * as MomentRange from 'moment-range'

export const FIELD_TYPES = {
  START_DATE: 'startDate',
  END_DATE: 'endDate'
} as const

const moment = MomentRange.extendMoment(momentJs as any)

export type FieldType = (typeof FIELD_TYPES)[keyof typeof FIELD_TYPES]

// use moment.parseZone to keep timezone data intact
const getEarliestPayPeriod = (
  earlierPayPeriod: moment.Moment | null,
  payPeriod: PayPeriodSchema
) => {
  const payPeriodStartDate = moment.parseZone(payPeriod.start_date)
  if (
    moment(payPeriodStartDate).isBefore(earlierPayPeriod) ||
    !earlierPayPeriod
  ) {
    return payPeriodStartDate
  }
  return earlierPayPeriod
}

const getLatestPayPeriod = (
  latestPayPeriod: moment.Moment | null,
  payPeriod: PayPeriodSchema
) => {
  const payPeriodEndDate = moment.parseZone(payPeriod.end_date)
  if (moment(payPeriodEndDate).isAfter(latestPayPeriod) || !latestPayPeriod) {
    return payPeriodEndDate
  }
  return latestPayPeriod
}

export const getPayPeriodsRange = (payPeriods?: PayPeriodSchema[]) => {
  if (!payPeriods || !payPeriods.length) {
    return null
  }

  // get first day or earlier pay period
  const firstDayOfFirstLockedPeriod = payPeriods.reduce<moment.Moment>(
    (earlierPayPeriod, payPeriod) => {
      return getEarliestPayPeriod(earlierPayPeriod || null, payPeriod)
    },
    getEarliestPayPeriod(null, payPeriods[0])
  )

  // get last day or latest pay period
  const lastDayOfLastPayPeriod = payPeriods.reduce<moment.Moment | null>(
    (latestPayPeriod, payPeriod) => {
      return getLatestPayPeriod(latestPayPeriod || null, payPeriod)
    },
    getLatestPayPeriod(null, payPeriods[0])
  )

  // return range
  return moment.range(
    moment(firstDayOfFirstLockedPeriod).startOf('day'),
    moment(lastDayOfLastPayPeriod).endOf('day')
  )
}

export function dateIsDisabled(
  contractDate,
  lockedPayPeriods: PayPeriodRecordMap
) {
  const lockedPayPeriodsRange = getPayPeriodsRange(
    lockedPayPeriods?.valueSeq().toJS()
  )

  if (!lockedPayPeriodsRange || !contractDate) {
    return false
  }

  // disable date if contained by the locked pay periods range
  return lockedPayPeriodsRange.contains(moment.parseZone(contractDate), {
    exclusive: true
  })
}

/**
 * Disable contract_time fields if its dates range of effect overlaps the locked
 * pay periods range
 *
 * If an amendment, or a contract has not end_date, we pass a date in 99 years
 * to avoid passing a null value to overlaps function
 *
 * @param savedStartDate
 * @param savedEndDate
 * @param lockedPayPeriods
 */
export function contractTimeIsDisabled(
  savedStartDate: string | null | undefined,
  savedEndDate: string | null | undefined,
  lockedPayPeriods: PayPeriodRecordMap | undefined
) {
  const lockedPayPeriodsRange = getPayPeriodsRange(
    lockedPayPeriods?.valueSeq().toJS()
  )

  if (!lockedPayPeriodsRange) {
    return false
  }

  if (!savedEndDate) {
    const range = moment.range(
      moment.parseZone(savedStartDate).startOf('day'),
      moment().add(99, 'years')
    )

    return range.overlaps(lockedPayPeriodsRange)
  }

  const contractRange = moment.range(
    moment.parseZone(savedStartDate).startOf('day'),
    moment.parseZone(savedEndDate).endOf('day')
  )

  return contractRange.overlaps(lockedPayPeriodsRange)
}

export function rangeIsValid(
  date: moment.Moment | string,
  savedStartDate: moment.Moment | string,
  savedEndDate: moment.Moment | string | null | undefined,
  formStartDate: moment.Moment | string,
  formEndDate: moment.Moment | string | null | undefined,
  lockedPayPeriods: PayPeriodRecordMap | undefined,
  fieldType: FieldType
) {
  const lockedPayPeriodsRange = getPayPeriodsRange(
    lockedPayPeriods?.valueSeq().toJS()
  )

  // Range will always be valid if no pay periods have been locked
  if (!lockedPayPeriodsRange) {
    return true
  }

  const parseDateWithTimeZone = value =>
    value ? moment.parseZone(value) : null

  // Determine the start and end dates of the new range
  const startDate =
    (fieldType === FIELD_TYPES.START_DATE && date) ||
    formStartDate ||
    savedStartDate
  const newStartDate = parseDateWithTimeZone(startDate)

  const endDate =
    (fieldType === FIELD_TYPES.END_DATE && date) || formEndDate || savedEndDate
  const newEndDate = parseDateWithTimeZone(endDate)

  // Create the new range based on the start and end dates
  const newRange = moment.range(
    newStartDate ? newStartDate.startOf('day') : moment().startOf('day'),
    newEndDate ? newEndDate.endOf('day') : moment().endOf('day')
  )

  // Create the old range based on the saved start and end dates
  const oldRange = moment.range(
    moment.parseZone(savedStartDate || moment()).startOf('day'),
    moment.parseZone(savedEndDate || moment()).endOf('day')
  )

  // Check if the new range intersects with the locked pay periods range
  const newIntersection = newRange.intersect(lockedPayPeriodsRange)
  const oldIntersection = oldRange.intersect(lockedPayPeriodsRange)

  // If both intersections are null, the range is valid
  if (!oldIntersection && !newIntersection) {
    return true
  }

  // If only one intersection is null, the range is invalid
  if (!oldIntersection || !newIntersection) {
    return false
  }

  // If the old and new intersections are the same, the range is valid
  return oldIntersection.isSame(newIntersection)
}
